import { Matrix4, Mesh, Sphere, Vector3 } from 'three';
import { ProjectRenderer } from '../ProjectRenderer';
import { CollidableObject } from '../objects/CollidableObject';

const SPHERE_RADIUS = 0.1;

export class CollisionHandler {
	constructor(private renderer: ProjectRenderer) {}

	checkCollisions(draggedModule: CollidableObject) {
		if (!draggedModule.collisionsEnabled) return;

		const oldCollidedModules = draggedModule.getCollidedModules();

		for (const collidable of this.renderer.modulesManager.getCollidableModules()) {
			if (collidable.id === draggedModule.id) continue;
			if (!collidable.collisionsEnabled) continue;

			const isColliding = this.detectCollisions(draggedModule, collidable);

			if (isColliding) {
				draggedModule.addCollidedModules(collidable.id);
				collidable.addCollidedModules(draggedModule.id);
			}

			if (!isColliding && oldCollidedModules.includes(collidable.id)) {
				draggedModule.removeCollidedModules(collidable.id);
				collidable.removeCollidedModules(draggedModule.id);
			}
		}
	}

	private detectCollisions(
		moduleA: CollidableObject,
		moduleB: CollidableObject
	) {
		const aabbCollision = this.checkAABBCollision(moduleA, moduleB);
		if (!aabbCollision) return false;

		const shapeCollision = this.checkShapeCollision(moduleA, moduleB);
		return shapeCollision;
	}

	private checkAABBCollision(
		moduleA: CollidableObject,
		moduleB: CollidableObject
	) {
		return moduleA.boundingBox.intersectsBox(moduleB.boundingBox);
	}

	private checkShapeCollision(
		moduleA: CollidableObject,
		moduleB: CollidableObject
	) {
		if (
			!moduleA.collisionZoneMesh.geometry.boundsTree ||
			!moduleB.collisionZoneMesh.geometry.boundsTree
		)
			throw new Error('MISSING BOUNDS TREE');

		if (
			this.isPointInsideMesh2D(
				moduleA.group.position,
				moduleB.collisionZoneMesh
			) ||
			this.isPointInsideMesh2D(
				moduleB.group.position,
				moduleA.collisionZoneMesh
			)
		)
			return true;

		let meshOne: Mesh, meshTwo: Mesh;

		meshOne = moduleA.collisionZoneMesh;
		meshTwo = moduleB.collisionZoneMesh;

		const matrix2to1 = new Matrix4()
			.copy(meshOne.matrixWorld)
			.invert()
			.multiply(meshTwo.matrixWorld);

		//@ts-ignore
		const hit = meshOne.geometry.boundsTree!.intersectsGeometry(
			meshTwo.geometry,
			matrix2to1
		);

		return hit;
	}

	private isPointInsideMesh2D(point: Vector3, mesh: Mesh) {
		const testSphere = new Sphere(
			new Vector3(point.x, mesh.position.y, point.z),
			SPHERE_RADIUS
		);

		mesh.updateWorldMatrix(true, false);
		const inverseMeshMatrix = new Matrix4().copy(mesh.matrixWorld).invert();

		testSphere.applyMatrix4(inverseMeshMatrix);

		//@ts-ignore
		const hit = mesh.geometry.boundsTree!.intersectsSphere(testSphere);

		return hit;
	}
}
